/**
 * Mandates-view audit-record filter.
 *
 * The Mandates view calls `filterRecords(records, query)` to apply the
 * search box + filter pills client-side. The server runs the same
 * filters; client-side is defense-in-depth so a server regression
 * doesn't leak the wrong rows.
 *
 * Searchable surface — kept aligned with what the row actually shows
 * on the card, so "what the merchant sees is what they can search":
 *   - record_id, site_id, policy_version
 *   - envelope_signature, prev_record_hash, merkle_root
 *   - decision text (so "rejected" / "approved" / "escalated" work)
 *   - intent action (so "place_order" / "browse_catalog" work)
 *   - retention_class (visible on every row)
 *   - the synthesized headline (so "Order placement", "Mandate rejected",
 *     "Signature verification failed", "Agent visit observed" all match)
 *   - registry agent id, visitor class and user agent (as the server)
 */

import { humanRecordDevice, identityFilter, isHumanVisit } from '@sill/audit';
import { zonedDayRangeUtc } from '@sill/types';
import type { AuditRecord } from '@sill/types';
import type { AuditQuery } from '@sill/types/api';

import { recordHeadline } from '../views/mandates/intent-label.js';

const empty_query: AuditQuery = {};

/**
 * Outcome group the row falls into for the status filter. Mirrors the
 * server's outcome→decision-set mapping so the client picks the same
 * subset the server returned.
 */
function outcomeGroup(record: AuditRecord): 'approved' | 'rejected' | 'escalated' | 'observed' {
  switch (record.decision) {
    case 'observed':
      return 'observed';
    case 'approved':
    // ADR-0157 (Bucket B Slice 2) — a successful settlement audit record
    // is the terminal positive-money outcome of an approved mandate.
    // Grouped with `approved` so the operator's status-pill filter picks
    // it up on the success side alongside the authorizing decision.
    case 'settled_succeeded':
    // ADR-0174 §2 (Slice 1) — a payment-link HANDOFF outcome is
    // authorized-then-handed-off: the mandate cleared policy but the
    // buyer completes payment on the merchant's own site. Grouped
    // with `approved` on the success side because the authorization
    // side succeeded (mirrors the `settled_succeeded` precedent).
    // Slice 3 finalizes the dashboard kicker copy distinguishing
    // "handoff" from "settled by Sill".
    case 'handoff_pending':
      return 'approved';
    case 'rejected':
    case 'verification_rejected':
    case 'rejected_post_verify':
    // ADR-0157 (Bucket B Slice 2) — a refund settlement is money moving
    // OUT (a reversal). Grouped with `rejected` so the reject/reversal
    // side of the status-pill filter captures refund settlements
    // alongside blocked and reject-class mandates. The refund MANDATE
    // itself (action='request_refund') still groups by its own
    // decision (approved / escalated_*); only the settlement record
    // for the refund lands here.
    case 'settled_refunded':
      return 'rejected';
    case 'escalated_approved':
    case 'escalated_rejected':
      return 'escalated';
  }
}

export function filterRecords<R extends AuditRecord>(
  records: readonly R[],
  query: AuditQuery = empty_query,
): R[] {
  const q = (query.q ?? '').trim().toLowerCase();
  const identity = query.identity ?? 'all';
  const status = query.status ?? 'all';
  // Calendar days in the display zone (query.tz), as UTC instants.
  const range = zonedDayRangeUtc(
    query.date_from !== undefined && query.date_from.length > 0 ? query.date_from : undefined,
    query.date_to !== undefined && query.date_to.length > 0 ? query.date_to : undefined,
    query.tz ?? 'UTC',
  );
  const from_ms = range.from_iso !== null ? Date.parse(range.from_iso) : null;
  const to_ms = range.to_iso !== null ? Date.parse(range.to_iso) : null;

  return records.filter((r) => {
    // Identity segment — the same shared predicate the server's SQL
    // mirrors (`identityFilter` in @sill/audit). The deprecated
    // envelope-signature `filter` is not a visitor concept and is no
    // longer applied here.
    if (!identityFilter(r, identity)) return false;

    // Status pill.
    if (status !== 'all' && outcomeGroup(r) !== status) {
      return false;
    }

    // Date range (inclusive). Compare ISO timestamp strings — the date
    // bounds are ISO-8601 dates (YYYY-MM-DD) which sort correctly when
    // compared to the YYYY-MM-DD prefix of an ISO timestamp.
    const at = Date.parse(r.evaluated_at);
    if (from_ms !== null && at < from_ms) return false;
    if (to_ms !== null && at > to_ms) return false;

    // Search query.
    if (q.length === 0) {
      return true;
    }
    const haystack = [
      r.record_id,
      r.site_id,
      r.mandate_id ?? '',
      r.policy_version,
      r.envelope_signature ?? '',
      r.prev_record_hash ?? '',
      r.merkle_root ?? '',
      r.decision,
      r.intent_summary?.action ?? '',
      r.retention_class,
      // Agent identity fields — keeps client-side filtering consistent with
      // the server's ILIKE clause. Needed so transactional records (whose
      // headline doesn't include the agent name) survive when a deep-link
      // from the Agents by Calls widget passes the raw agent_id slug or
      // visitor_class as `q`.
      r.identification_match?.agent_id ?? '',
      r.identification_match?.visitor_class ?? '',
      // The server's ILIKE also matches the user agent; without it here a
      // search like "python" or "curl" got rows from the server that this
      // pass then dropped (list shorter than the count; filters sweep
      // 2026-10-14).
      r.user_agent ?? '',
      r.page_path ?? '',
      recordHeadline(r),
      // A human visit is named by place; keep "chrome" / "iphone" searchable.
      isHumanVisit(r) ? (humanRecordDevice(r) ?? '') : '',
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}
